import { useContext, useMemo } from "react";

import { DateLocale, type IDateLocale } from "../AppProvider.types";
import { BuilderInfoContext } from "../globalContext/BuilderInfoContext";

interface IDateFnsLocale extends IDateLocale {
    dateFormat: "PP";
    dateTimeFormat: "PPp";
    /** Need to set `useAdditionalWeekYearTokens` to `true` when using format from date-fns */
    weekFormat: "wo 'week of' YYYY";
    monthFormat: "MMM yyyy";
}

export function useDateFnsLocale() {
    const builderInfo = useContext(BuilderInfoContext);
    const builderInputDateFormat =
        builderInfo?.locale.dateLocale?.builderInputDateFormat ??
        DateLocale.default.builderInputDateFormat;
    return useMemo<IDateFnsLocale>(() => {
        const dateFormat = "PP";
        const separators = ["-", ".", " ", "/"];
        // all values are used for parsing and first value is used for formatting
        // this allows users to type in their locale culture format (example: M-D-YYYY for USA)
        const dateInputFormats = [
            dateFormat,
            "PPP",
            ...separators.flatMap((separator) => {
                const withSeparator = builderInputDateFormat
                    .replaceAll("Y", "y")
                    .replaceAll("D", "d")
                    .replace(/[-./ ]/g, separator);
                const nonLeadingZero = withSeparator.replace("MM", "M").replace("dd", "d");
                const leadingZero = nonLeadingZero.replace("M", "MM").replace("d", "dd");
                return [nonLeadingZero, leadingZero];
            }),
        ];

        return {
            dateFormat,
            dateTimeFormat: "PPp",
            weekFormat: "wo 'week of' YYYY",
            monthFormat: "MMM yyyy",

            /** List of acceptable format strings for date inputs */
            dateInputFormats,
        };
    }, [builderInputDateFormat]);
}
